/*
  Navigation v2 — sections/header-v2.liquid.

  <nav-v2-mega>   a desktop menu item with a panel: opens on hover (pointer
                  devices) or its + button, closes on leave / Escape /
                  focus-out, keeps the sticky header from hiding while open.
                  No <details> anywhere in nav v2: global.js attaches a
                  height-animating Accordion() to every <details> on the page.
  <nav-v2-tabs>   the products mega panel: rail tabs (hover or click), goal
                  tiles and chips.
  <nav-v2-drawer> the mobile drawer: toggle, focus trap, body scroll lock,
                  one-open-at-a-time accordions, default accordion on open.

  trapFocus / removeTrapFocus come from assets/global.js.
*/
(() => {
  const HOVER_DELAY = 150;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  class NavV2Mega extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-nv2-mega-toggle]');
      this.panel = this.querySelector('[data-nv2-mega-panel]');
      if (!this.toggle || !this.panel) return;
      this.headerWrapper = this.closest('.header-wrapper');
      this.closeTimer = null;

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.addEventListener('mouseenter', () => {
        if (!canHover() || !isDesktop()) return;
        window.clearTimeout(this.closeTimer);
        this.open();
      });
      this.addEventListener('mouseleave', () => {
        if (!canHover()) return;
        this.closeTimer = window.setTimeout(() => this.close(), HOVER_DELAY);
      });
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.close();
        this.toggle.focus();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      if (this.closeAnimation) {
        this.closeAnimation.cancel();
        this.closeAnimation = null;
        this.panel.style.pointerEvents = '';
      }
      // Only one panel open at a time.
      document.querySelectorAll('nav-v2-mega').forEach((other) => {
        if (other !== this) other.close();
      });
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      const tabs = this.querySelector('nav-v2-tabs');
      if (tabs && typeof tabs.ensureMeasured === 'function') tabs.ensureMeasured();
      if (this.headerWrapper) {
        this.headerWrapper.preventHide = true;
        document.documentElement.style.setProperty(
          '--header-bottom-position-desktop',
          `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`
        );
      }
    }

    close() {
      window.clearTimeout(this.closeTimer);
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
      const finish = () => {
        this.panel.hidden = true;
        this.panel.style.pointerEvents = '';
        const tabs = this.querySelector('nav-v2-tabs');
        if (tabs && typeof tabs.reset === 'function') tabs.reset();
      };
      if (reducedMotion() || typeof this.panel.animate !== 'function') {
        finish();
        return;
      }
      this.panel.style.pointerEvents = 'none';
      const fade = this.panel.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-0.8rem)' },
        ],
        { duration: 160, easing: 'ease-in' }
      );
      this.closeAnimation = fade;
      // Finalize on finish, or after a timeout: animations do not advance in
      // background tabs, and the panel must not stay in limbo.
      const done = () => {
        if (this.closeAnimation !== fade) return;
        this.closeAnimation = null;
        finish();
      };
      fade.onfinish = done;
      window.setTimeout(done, 400);
    }
  }

  class NavV2Tabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-nv2-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-nv2-panel]'));
      this.goalTiles = this.querySelector('[data-nv2-goal-tiles]');
      this.goalViews = Array.from(this.querySelectorAll('[data-nv2-goal-view]'));
      this.defaultTab = this.dataset.default || 'best';

      this.measuredWidth = 0;
      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          const megaPanel = this.closest('[data-nv2-mega-panel]');
          if (megaPanel && !megaPanel.hidden) this.ensureMeasured();
        }, 120);
      };
      window.addEventListener('resize', this.onResize, { passive: true });

      // Hovering a rail tab (pointer devices) switches the panel after a short
      // delay; the state then stays until another tab is hovered or clicked,
      // so moving the mouse into the panel keeps what was shown.
      this.hoverTimer = null;
      this.addEventListener('mouseover', (event) => {
        if (!canHover()) return;
        const tab = event.target.closest('[data-nv2-tab]');
        if (!tab || !this.contains(tab) || tab.getAttribute('aria-selected') === 'true') return;
        window.clearTimeout(this.hoverTimer);
        this.hoverTimer = window.setTimeout(() => this.showTab(tab.dataset.nv2Tab), 80);
      });

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-nv2-tab]');
        if (tab && this.contains(tab)) {
          // A tab that is a link (Bestsellers with a "Tab link") opens its page where
          // hovering already showed the panel, or once its panel is open on touch.
          if (tab.hasAttribute('href') && (canHover() || tab.getAttribute('aria-selected') === 'true')) return;
          event.preventDefault();
          window.clearTimeout(this.hoverTimer);
          this.showTab(tab.dataset.nv2Tab);
          return;
        }
        const goal = event.target.closest('[data-nv2-goal]');
        if (goal && this.contains(goal)) this.showGoal(goal.dataset.nv2Goal);
      });

      this.reset();
    }

    showTab(id) {
      if (!this.tabs.some((tab) => tab.dataset.nv2Tab === id)) id = this.defaultTab;
      this.tabs.forEach((tab) => {
        const on = tab.dataset.nv2Tab === id;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.nv2Panel !== id;
      });
      if (id === 'goals') this.showGoal(null);
    }

    showGoal(index) {
      if (this.goalTiles) this.goalTiles.hidden = Boolean(index);
      this.goalViews.forEach((view) => {
        view.hidden = view.dataset.nv2GoalView !== index;
      });
    }

    reset() {
      this.showTab(this.defaultTab);
    }

    // Lock the panels area to the tallest tab (and tallest goal view) for
    // the current viewport width, so switching tabs does not move the panel
    // edge. Only meaningful while the mega panel is displayed.
    ensureMeasured() {
      if (this.measuredWidth === window.innerWidth) return;
      if (this.offsetParent === null && this.getClientRects().length === 0) return;
      const panelState = this.panels.map((panel) => panel.hidden);
      const viewState = this.goalViews.map((view) => view.hidden);
      const tilesState = this.goalTiles ? this.goalTiles.hidden : null;
      this.classList.add('is-measuring');
      let max = 0;
      this.panels.forEach((panel) => {
        panel.hidden = false;
        if (panel.dataset.nv2Panel === 'goals') {
          if (this.goalTiles) this.goalTiles.hidden = false;
          this.goalViews.forEach((view) => (view.hidden = true));
          max = Math.max(max, panel.offsetHeight);
          if (this.goalTiles) this.goalTiles.hidden = true;
          this.goalViews.forEach((view) => {
            view.hidden = false;
            max = Math.max(max, panel.offsetHeight);
            view.hidden = true;
          });
        } else {
          max = Math.max(max, panel.offsetHeight);
        }
        panel.hidden = true;
      });
      this.panels.forEach((panel, i) => (panel.hidden = panelState[i]));
      this.goalViews.forEach((view, i) => (view.hidden = viewState[i]));
      if (this.goalTiles) this.goalTiles.hidden = tilesState;
      this.classList.remove('is-measuring');
      if (max > 0) this.style.setProperty('--nv2-panel-height', `${Math.ceil(max)}px`);
      this.measuredWidth = window.innerWidth;
    }
  }

  class NavV2Drawer extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-nv2-toggle]');
      this.panel = this.querySelector('[data-nv2-drawer]');
      if (!this.toggle || !this.panel) return;
      this.accordions = Array.from(this.querySelectorAll('[data-nv2-acc]'));
      this.header = document.querySelector('.section-header');
      this.headerWrapper = this.closest('.header-wrapper');
      this.onResize = this.onResize.bind(this);

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      const backdrop = this.querySelector('[data-nv2-backdrop]');
      if (backdrop) backdrop.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.close();
        this.toggle.focus();
      });
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (this.isOpen && !this.contains(document.activeElement)) this.close();
        });
      });
      window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
        if (event.matches && this.isOpen) this.close();
      });

      // One accordion open at a time.
      this.accordions.forEach((acc) => {
        const button = acc.querySelector('[data-nv2-acc-toggle]');
        if (!button) return;
        button.addEventListener('click', () => {
          const opening = !acc.classList.contains('is-open');
          this.accordions.forEach((other) => this.setAccordion(other, other === acc && opening));
        });
      });

      // Goal rows on mobile that expand their products inside the menu.
      this.goalRows = Array.from(this.querySelectorAll('[data-nv2-goal-row]')).filter((row) =>
        row.querySelector('[data-nv2-goal-toggle]')
      );
      this.goalRows.forEach((row) => {
        row.querySelector('[data-nv2-goal-toggle]').addEventListener('click', () => {
          const opening = !row.classList.contains('is-open');
          this.goalRows.forEach((other) => this.setGoalRow(other, other === row && opening));
        });
      });
    }

    setGoalRow(row, open, animate = true) {
      this.setAccordion(row, open, animate, '[data-nv2-goal-toggle]', '.nv2-goal-row__panel');
    }

    setAccordion(acc, open, animate = true, toggleSelector = '[data-nv2-acc-toggle]', panelSelector = '.nv2-acc__panel') {
      const button = acc.querySelector(toggleSelector);
      const panel = acc.querySelector(panelSelector);
      if (button) button.setAttribute('aria-expanded', String(open));
      if (acc._nv2Animation) {
        acc._nv2Animation.cancel();
        acc._nv2Animation = null;
      }
      const instant = !animate || !panel || reducedMotion() || typeof panel.animate !== 'function';
      if (instant) {
        acc.classList.toggle('is-open', open);
        if (panel) panel.classList.remove('is-animating');
        return;
      }
      panel.classList.add('is-animating');
      let animation;
      if (open) {
        acc.classList.add('is-open');
        const height = panel.scrollHeight;
        animation = panel.animate(
          [
            { height: '0px', opacity: 0 },
            { height: `${height}px`, opacity: 1 },
          ],
          { duration: 280, easing: 'ease-out' }
        );
      } else {
        const height = panel.offsetHeight;
        animation = panel.animate(
          [
            { height: `${height}px`, opacity: 1 },
            { height: '0px', opacity: 0 },
          ],
          { duration: 220, easing: 'ease-in' }
        );
      }
      acc._nv2Animation = animation;
      const done = () => {
        if (acc._nv2Animation !== animation) return;
        acc._nv2Animation = null;
        panel.classList.remove('is-animating');
        if (!open) acc.classList.remove('is-open');
      };
      animation.onfinish = done;
      window.setTimeout(done, 600);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    setPositions() {
      if (this.header) {
        const borderOffset = this.headerWrapper && this.headerWrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - borderOffset, 10)}px`
        );
      }
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    onResize() {
      this.setPositions();
    }

    open() {
      this.setPositions();
      window.addEventListener('resize', this.onResize, { passive: true });
      this.setAttribute('open', '');
      this.toggle.setAttribute('aria-expanded', 'true');
      if (this.header) this.header.classList.add('menu-open');
      document.body.classList.add('overflow-hidden-tablet');
      // Reset the accordions: the default one open, the rest closed (no animation).
      this.accordions.forEach((acc) => this.setAccordion(acc, acc.hasAttribute('data-nv2-default'), false));
      this.goalRows.forEach((row) => this.setGoalRow(row, false, false));
      this.panel.scrollTop = 0;
      if (typeof trapFocus === 'function') {
        const first = this.panel.querySelector('button, a');
        trapFocus(this, first || this.panel);
      }
    }

    close() {
      this.removeAttribute('open');
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.header) this.header.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden-tablet');
      window.removeEventListener('resize', this.onResize);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);
    }
  }

  if (!customElements.get('nav-v2-mega')) customElements.define('nav-v2-mega', NavV2Mega);
  if (!customElements.get('nav-v2-tabs')) customElements.define('nav-v2-tabs', NavV2Tabs);
  if (!customElements.get('nav-v2-drawer')) customElements.define('nav-v2-drawer', NavV2Drawer);
})();
